// Web Speech API type declarations
declare global {
  interface Window {
    webkitSpeechRecognition: any;
    SpeechRecognition: any;
  }
}

interface SpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  maxAlternatives: number;
  start(): void;
  stop(): void;
  onstart: ((this: SpeechRecognition, ev: Event) => any) | null;
  onresult: ((this: SpeechRecognition, ev: SpeechRecognitionEvent) => any) | null;
  onerror: ((this: SpeechRecognition, ev: SpeechRecognitionErrorEvent) => any) | null;
  onend: ((this: SpeechRecognition, ev: Event) => any) | null;
}

interface SpeechRecognitionEvent extends Event {
  readonly results: SpeechRecognitionResultList;
}

interface SpeechRecognitionResultList {
  readonly length: number;
  [index: number]: SpeechRecognitionResult;
}

interface SpeechRecognitionResult {
  readonly isFinal: boolean;
  readonly length: number;
  [index: number]: SpeechRecognitionAlternative;
}

interface SpeechRecognitionAlternative {
  readonly transcript: string;
  readonly confidence: number;
}

interface SpeechRecognitionErrorEvent extends Event {
  readonly error: string;
  readonly message: string;
}

interface TranscriptionSegment {
  id: string;
  text: string;
  confidence: number;
  timestamp: number;
  isFinal: boolean;
  speaker?: string;
  duration?: number;
}

interface TranscriptionSession {
  sessionId: string;
  segments: TranscriptionSegment[];
  fullText: string;
  startTime: Date;
  language: string;
  totalWords: number;
  avgConfidence: number;
  filteredSegments?: any[];
  filteringStats?: any;
  sessionContext?: {
    patientId?: string;
    sessionType?: string;
    appointmentReason?: string;
  };
}

interface TranscriptionConfig {
  language: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  enableSpeakerDiarization: boolean;
  enablePunctuation: boolean;
  enableProfanityFilter: boolean;
  enableContentFiltering: boolean;
  contentFilteringThreshold: number;
}

class RealTimeTranscriptionService {
  private recognition: SpeechRecognition | null = null;
  private isTranscribing = false;
  private currentSession: TranscriptionSession | null = null;
  private segmentCounter = 0;
  private onTranscriptCallback?: (segment: TranscriptionSegment) => void;
  private onSessionUpdateCallback?: (session: TranscriptionSession) => void;
  private onErrorCallback?: (error: string) => void;
  private restartTimeout: NodeJS.Timeout | null = null;

  private config: TranscriptionConfig = {
    language: 'en-US',
    continuous: true,
    interimResults: true,
    maxAlternatives: 3,
    enableSpeakerDiarization: false,
    enablePunctuation: true,
    enableProfanityFilter: true,
    enableContentFiltering: true,
    contentFilteringThreshold: 0.6
  };

  constructor() {
    this.initializeSpeechRecognition();
  }

  /**
   * Initialize Web Speech API
   */
  private initializeSpeechRecognition(): boolean {
    console.log('🎤 Initializing Speech Recognition...');

    // Check for browser support
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      console.error('Speech recognition not supported in this browser. Please use Chrome, Edge, or Safari.');
      (window as any).dispatchEvent(new CustomEvent('app-toast', { detail: { type: 'warning', title: 'Browser Not Supported', message: 'Speech recognition is not supported in this browser. Please use Chrome, Edge, or Safari for live transcription.' } }));
      return false;
    }

    console.log('✅ Speech Recognition API available');

    const SpeechRecognition = (window as any).webkitSpeechRecognition || (window as any).SpeechRecognition;
    this.recognition = new SpeechRecognition();

    // Configure recognition
    if (this.recognition) {
      this.recognition.continuous = this.config.continuous;
      this.recognition.interimResults = this.config.interimResults;
      this.recognition.lang = this.config.language;
      this.recognition.maxAlternatives = this.config.maxAlternatives;

      console.log('✅ Speech Recognition configured:', {
        continuous: this.config.continuous,
        interimResults: this.config.interimResults,
        language: this.config.language
      });
    }

    // Event handlers
    if (this.recognition) {
      this.recognition.onstart = () => {
        console.log('🎙️ Real-time transcription started successfully');
        this.isTranscribing = true;
      };

      this.recognition.onresult = (event: SpeechRecognitionEvent) => {
        try {
          this.handleTranscriptionResult(event);
        } catch (error) {
          console.error('Error handling transcription result:', error);
          this.onErrorCallback?.(`Error processing speech: ${error}`);
        }
      };

      this.recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
        console.error('Transcription error:', event.error, event.message);

        // Handle different error types
        switch (event.error) {
          case 'no-speech':
            console.log('No speech detected, continuing...');
            // Don't show error for no-speech, just continue
            break;

          case 'aborted':
            console.log('Recognition aborted, will attempt restart...');
            // Don't immediately restart - let onend handle it
            break;

          case 'not-allowed':
            this.onErrorCallback?.('Microphone access denied. Please allow microphone access.');
            this.isTranscribing = false;
            break;

          case 'network':
            this.onErrorCallback?.('Network error occurred during transcription.');
            if (this.currentSession && !this.restartTimeout) {
              this.restartTimeout = setTimeout(() => {
                this.restartTranscription();
                this.restartTimeout = null;
              }, 2000);
            }
            break;

          default:
            this.onErrorCallback?.(`Transcription error: ${event.error}`);
            console.error('Unhandled transcription error:', event.error);
            break;
        }
      };

      this.recognition.onend = () => {
        console.log('Transcription session ended');
        const wasTranscribing = this.isTranscribing;
        this.isTranscribing = false;

        // Auto-restart if session is still active and we were transcribing
        if (this.currentSession && wasTranscribing && !this.restartTimeout) {
          this.restartTimeout = setTimeout(() => {
            this.restartTranscription();
            this.restartTimeout = null;
          }, 1000);
        }
      };
    }

    return true;
  }

  /**
   * Start real-time transcription for a session
   */
  async startTranscription(sessionId: string, callbacks: {
    onTranscript?: (segment: TranscriptionSegment) => void;
    onSessionUpdate?: (session: TranscriptionSession) => void;
    onError?: (error: string) => void;
  } = {}, sessionContext?: {
    patientId?: string;
    sessionType?: string;
    appointmentReason?: string;
  }): Promise<boolean> {
    try {
      if (!this.recognition) {
        throw new Error('Speech recognition not available');
      }

      // Set callbacks
      this.onTranscriptCallback = callbacks.onTranscript;
      this.onSessionUpdateCallback = callbacks.onSessionUpdate;
      this.onErrorCallback = callbacks.onError;

      // Initialize session
      this.currentSession = {
        sessionId,
        segments: [],
        fullText: '',
        startTime: new Date(),
        language: this.config.language,
        totalWords: 0,
        avgConfidence: 0,
        sessionContext
      };

      this.segmentCounter = 0;

      // Start recognition with error handling
      try {
        console.log('🚀 Starting speech recognition...');
        this.recognition.start();
        console.log('✅ Speech recognition start() called successfully');
        return true;
      } catch (error) {
        console.error('❌ Failed to start speech recognition:', error);
        this.onErrorCallback?.(`Failed to start recognition: ${error}`);
        return false;
      }

    } catch (error) {
      console.error('Failed to start transcription:', error);
      this.onErrorCallback?.(`Failed to start transcription: ${error}`);
      return false;
    }
  }

  /**
   * Stop transcription and apply content filtering
   */
  async stopTranscription(): Promise<TranscriptionSession | null> {
    // Clear any pending restart timeout
    if (this.restartTimeout) {
      clearTimeout(this.restartTimeout);
      this.restartTimeout = null;
    }

    if (this.recognition && this.isTranscribing) {
      this.recognition.stop();
      this.isTranscribing = false;
    }

    const session = this.currentSession;
    if (session && this.config.enableContentFiltering) {
      await this.applyContentFiltering(session);
    }

    this.currentSession = null;
    return session;
  }

  /**
   * Handle speech recognition results
   */
  private handleTranscriptionResult(event: SpeechRecognitionEvent): void {
    console.log('📝 Transcription result received');

    if (!this.currentSession) {
      console.warn('⚠️ No current session for transcription result');
      return;
    }

    const results = event.results;
    const lastResult = results[results.length - 1];

    console.log('📊 Result details:', {
      totalResults: results.length,
      lastResultIndex: results.length - 1,
      isFinal: lastResult?.isFinal
    });

    if (lastResult) {
      const transcript = lastResult[0].transcript;
      const confidence = lastResult[0].confidence || 0.8; // Fallback confidence
      const isFinal = lastResult.isFinal;

      console.log('💬 Transcript received:', {
        text: transcript,
        confidence: Math.round(confidence * 100) + '%',
        isFinal,
        length: transcript.length
      });

      // Create transcription segment
      const segment: TranscriptionSegment = {
        id: `segment_${this.segmentCounter++}`,
        text: transcript.trim(),
        confidence,
        timestamp: Date.now(),
        isFinal,
        speaker: this.detectSpeaker(transcript), // Simple speaker detection
        duration: transcript.split(' ').length * 0.5 // Estimate duration
      };

      // Add to session if final
      if (isFinal && segment.text.length > 0) {
        this.currentSession.segments.push(segment);
        this.updateSessionMetrics();
      }

      // Notify callback
      this.onTranscriptCallback?.(segment);

      // Update session
      if (isFinal) {
        this.onSessionUpdateCallback?.(this.currentSession);
      }
    }
  }

  /**
   * Simple speaker detection based on audio patterns
   */
  private detectSpeaker(text: string): string {
    // In a real implementation, this would use voice characteristics
    // For now, we'll use simple heuristics
    if (text.includes('patient') || text.includes('I feel') || text.includes('my symptoms')) {
      return 'Patient';
    } else if (text.includes('doctor') || text.includes('medical') || text.includes('prescription')) {
      return 'Provider';
    }
    return 'Unknown';
  }

  /**
   * Update session-level metrics
   */
  private updateSessionMetrics(): void {
    if (!this.currentSession) return;

    // Calculate total words
    this.currentSession.totalWords = this.currentSession.segments.reduce(
      (total, segment) => total + segment.text.split(' ').length, 0
    );

    // Calculate average confidence
    if (this.currentSession.segments.length > 0) {
      this.currentSession.avgConfidence = this.currentSession.segments.reduce(
        (total, segment) => total + segment.confidence, 0
      ) / this.currentSession.segments.length;
    }

    // Build full text
    this.currentSession.fullText = this.currentSession.segments
      .map(segment => segment.text)
      .join(' ');
  }

  /**
   * Restart transcription after error
   */
  private restartTranscription(): void {
    if (this.currentSession && !this.isTranscribing && this.recognition) {
      console.log('Restarting transcription...');
      try {
        this.recognition.start();
        console.log('Transcription restarted successfully');
      } catch (error) {
        console.error('Failed to restart transcription:', error);
        this.onErrorCallback?.(`Failed to restart transcription: ${error}`);

        // If restart fails, try again after a longer delay (max 3 attempts)
        if (!this.restartTimeout) {
          this.restartTimeout = setTimeout(() => {
            this.restartTimeout = null;
            if (this.currentSession) {
              console.log('Retrying transcription restart...');
              this.restartTranscription();
            }
          }, 3000);
        }
      }
    }
  }

  /**
   * Update transcription configuration
   */
  updateConfig(newConfig: Partial<TranscriptionConfig>): void {
    this.config = { ...this.config, ...newConfig };

    if (this.recognition) {
      this.recognition.continuous = this.config.continuous;
      this.recognition.interimResults = this.config.interimResults;
      this.recognition.lang = this.config.language;
      this.recognition.maxAlternatives = this.config.maxAlternatives;
    }
  }

  /**
   * Get current transcription status
   */
  getStatus(): {
    isTranscribing: boolean;
    currentSession: TranscriptionSession | null;
    wordsPerMinute: number;
    sessionDuration: number;
  } {
    const sessionDuration = this.currentSession
      ? Date.now() - this.currentSession.startTime.getTime()
      : 0;

    const wordsPerMinute = this.currentSession && sessionDuration > 0
      ? (this.currentSession.totalWords / (sessionDuration / 60000))
      : 0;

    return {
      isTranscribing: this.isTranscribing,
      currentSession: this.currentSession,
      wordsPerMinute,
      sessionDuration: sessionDuration / 1000 // in seconds
    };
  }

  /**
   * Export transcription in various formats
   */
  exportTranscription(format: 'txt' | 'json' | 'srt' | 'vtt'): string {
    if (!this.currentSession) return '';

    switch (format) {
      case 'txt':
        return this.currentSession.fullText;

      case 'json':
        return JSON.stringify(this.currentSession, null, 2);

      case 'srt':
        return this.generateSRT();

      case 'vtt':
        return this.generateVTT();

      default:
        return this.currentSession.fullText;
    }
  }

  /**
   * Generate SRT subtitle format
   */
  private generateSRT(): string {
    if (!this.currentSession) return '';

    let srt = '';
    let index = 1;
    let currentTime = 0;

    for (const segment of this.currentSession.segments) {
      const startTime = this.formatSRTTime(currentTime);
      currentTime += segment.duration || 3;
      const endTime = this.formatSRTTime(currentTime);

      srt += `${index}\n`;
      srt += `${startTime} --> ${endTime}\n`;
      srt += `${segment.text}\n\n`;
      index++;
    }

    return srt;
  }

  /**
   * Generate WebVTT format
   */
  private generateVTT(): string {
    if (!this.currentSession) return '';

    let vtt = 'WEBVTT\n\n';
    let currentTime = 0;

    for (const segment of this.currentSession.segments) {
      const startTime = this.formatVTTTime(currentTime);
      currentTime += segment.duration || 3;
      const endTime = this.formatVTTTime(currentTime);

      vtt += `${startTime} --> ${endTime}\n`;
      vtt += `${segment.text}\n\n`;
    }

    return vtt;
  }

  /**
   * Format time for SRT (HH:MM:SS,mmm)
   */
  private formatSRTTime(seconds: number): string {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    const ms = Math.floor((seconds % 1) * 1000);

    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')},${ms.toString().padStart(3, '0')}`;
  }

  /**
   * Format time for VTT (HH:MM:SS.mmm)
   */
  private formatVTTTime(seconds: number): string {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    const ms = Math.floor((seconds % 1) * 1000);

    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}.${ms.toString().padStart(3, '0')}`;
  }

  /**
   * Search within transcription
   */
  searchTranscription(query: string): TranscriptionSegment[] {
    if (!this.currentSession) return [];

    const lowerQuery = query.toLowerCase();
    return this.currentSession.segments.filter(segment =>
      segment.text.toLowerCase().includes(lowerQuery)
    );
  }

  /**
   * Get transcription health and diagnostic information
   */
  getHealthStatus(): {
    isHealthy: boolean;
    isTranscribing: boolean;
    hasSession: boolean;
    recognitionSupported: boolean;
    lastError: string | null;
    restartCount: number;
    uptime: number; // seconds since start
  } {
    const uptime = this.currentSession
      ? (Date.now() - this.currentSession.startTime.getTime()) / 1000
      : 0;

    return {
      isHealthy: this.isTranscribing && this.currentSession !== null && this.recognition !== null,
      isTranscribing: this.isTranscribing,
      hasSession: this.currentSession !== null,
      recognitionSupported: 'webkitSpeechRecognition' in window || 'SpeechRecognition' in window,
      lastError: null, // Could track last error if needed
      restartCount: 0, // Could track restart attempts
      uptime
    };
  }

  /**
   * Apply content filtering to session segments
   */
  private async applyContentFiltering(session: TranscriptionSession): Promise<void> {
    try {
      // Import the content filtering service
      const { contentFilteringService } = await import('./ContentFilteringService');

      // Configure filtering based on transcription config
      contentFilteringService.updateConfig({
        minRelevanceThreshold: this.config.contentFilteringThreshold,
        enableMedicalTermFiltering: true,
        enableInterruptionFiltering: true,
        enablePersonalInfoFiltering: true,
        enableAdministrativeFiltering: false,
        preserveCompleteStatements: true,
        contextWindowSize: 3
      });

      // Apply content filtering
      const filteringResult = await contentFilteringService.filterTranscriptionContent(
        session.segments,
        session.sessionContext
      );

      // Update session with filtered content
      session.filteredSegments = filteringResult.filteredSegments;
      session.filteringStats = filteringResult.stats;

      // Update full text with only relevant content
      const relevantText = filteringResult.filteredSegments
        .filter(s => s.isRelevant && s.filteredText)
        .map(s => s.filteredText)
        .join(' ');

      if (relevantText) {
        session.fullText = relevantText;
      }

      console.log('Content filtering applied:', filteringResult.stats);
    } catch (error) {
      console.error('Failed to apply content filtering:', error);
    }
  }

  /**
   * Get filtered transcription content
   */
  async getFilteredContent(format: 'clean-text' | 'medical-only' | 'full-report' | 'json' = 'clean-text'): Promise<string> {
    if (!this.currentSession?.filteredSegments) {
      return this.currentSession?.fullText || '';
    }

    // Import and use content filtering service for export
    return await this.exportFilteredContent(this.currentSession.filteredSegments, format);
  }

  /**
   * Export filtered content using content filtering service
   */
  private async exportFilteredContent(filteredSegments: any[], format: string): Promise<string> {
    try {
      const { contentFilteringService } = await import('./ContentFilteringService');
      return contentFilteringService.exportFilteredContent(filteredSegments, format as any);
    } catch (error) {
      console.error('Failed to export filtered content:', error);
      return '';
    }
  }

  /**
   * Get content filtering statistics
   */
  getFilteringStats(): any {
    return this.currentSession?.filteringStats || null;
  }

  /**
   * Enable or disable real-time content filtering
   */
  setContentFilteringEnabled(enabled: boolean, threshold: number = 0.6): void {
    this.config.enableContentFiltering = enabled;
    this.config.contentFilteringThreshold = threshold;
  }

  /**
   * Get transcription statistics
   */
  getTranscriptionStats(): {
    totalSegments: number;
    totalWords: number;
    avgConfidence: number;
    speakerBreakdown: Record<string, number>;
    lowConfidenceSegments: TranscriptionSegment[];
  } {
    if (!this.currentSession) {
      return {
        totalSegments: 0,
        totalWords: 0,
        avgConfidence: 0,
        speakerBreakdown: {},
        lowConfidenceSegments: []
      };
    }

    const speakerBreakdown: Record<string, number> = {};
    const lowConfidenceSegments: TranscriptionSegment[] = [];

    this.currentSession.segments.forEach(segment => {
      // Speaker breakdown
      if (segment.speaker) {
        speakerBreakdown[segment.speaker] = (speakerBreakdown[segment.speaker] || 0) + 1;
      }

      // Low confidence segments (below 70%)
      if (segment.confidence < 0.7) {
        lowConfidenceSegments.push(segment);
      }
    });

    return {
      totalSegments: this.currentSession.segments.length,
      totalWords: this.currentSession.totalWords,
      avgConfidence: this.currentSession.avgConfidence,
      speakerBreakdown,
      lowConfidenceSegments
    };
  }
}

export const transcriptionService = new RealTimeTranscriptionService();
export type { TranscriptionSegment, TranscriptionSession, TranscriptionConfig };